import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  Grid,
  Pagination,
  CircularProgress,
  Alert,
  Avatar,
  Breadcrumbs,
  Link,
  Paper
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import { 
  ArrowBack,
  Schedule,
  ShoppingCart,
  Article,
  Business
} from '@mui/icons-material';
import { getOffersByBrand } from '../services/offerService';
import { formatDistanceToNow } from 'date-fns';

const OffersByBrand = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  
  const [offers, setOffers] = useState([]);
  const [brand, setBrand] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const itemsPerPage = 12;

  useEffect(() => {
    fetchOffers(currentPage);
  }, [brandId, currentPage]);

  const fetchOffers = async (page) => {
    try {
      setLoading(true);
      const response = await getOffersByBrand(brandId, page, itemsPerPage);
      setOffers(response.offers);
      setBrand(response.brand);
      setTotalPages(response.totalPages);
      setCurrentPage(response.currentPage);
      setError(null);
    } catch (err) {
      setError('Failed to fetch offers for this brand');
      console.error('Error fetching offers by brand:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const isOfferExpired = (expirationDate) => {
    return new Date(expirationDate) < new Date();
  };

  const formatExpirationDate = (expirationDate) => {
    const expiry = new Date(expirationDate);
    const now = new Date();
    
    if (expiry < now) {
      return 'Expired';
    }
    
    return `Expires ${formatDistanceToNow(expiry, { addSuffix: true })}`;
  };

  if (loading && currentPage === 1) {
    return (
      <DashboardLayout title="Loading..." maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout title="Error" maxWidth="lg">
        <Box sx={{ maxWidth: 'lg', mx: 'auto', px: 3, py: 2 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button
            onClick={() => navigate('/offers')}
            startIcon={<ArrowBack />}
            variant="outlined"
          >
            Back to All Offers
          </Button>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={brand ? `${brand.name} Offers` : "Brand Offers"} maxWidth="lg">
      <Box sx={{ maxWidth: 'lg', mx: 'auto', px: 3, py: 2 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            onClick={() => navigate('/offers')}
            startIcon={<ArrowBack />}
            variant="outlined"
            sx={{ mb: 3 }}
          >
            Back to All Offers
          </Button>

        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/offers" underline="hover">
            Offers
          </Link>
          <Typography color="text.primary">By Brand</Typography>
          {brand && (
            <Typography color="text.primary">{brand.name}</Typography>
          )}
        </Breadcrumbs>

        {brand && (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              {brand.logo && (
                <Avatar 
                  src={brand.logo} 
                  sx={{ width: 64, height: 64 }}
                  alt={brand.name}
                />
              )}
              <Box>
                <Typography variant="h4" component="h1" gutterBottom>
                  <Business sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
                  {brand.name} Offers
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  {offers.length} offer{offers.length !== 1 ? 's' : ''} available
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Offers Grid */}
      {offers.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No offers found for this brand
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            There are currently no active offers from this brand. Check back later!
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/offers')}
            sx={{ mt: 2 }}
          >
            Browse All Offers
          </Button>
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {offers.map((offer) => (
              <Grid item xs={12} sm={6} md={4} key={offer._id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    opacity: isOfferExpired(offer.expirationDate) ? 0.6 : 1
                  }}
                >
                  {offer.featuredImage && (
                    <Box
                      sx={{
                        height: 200,
                        backgroundImage: `url(${offer.featuredImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        position: 'relative'
                      }}
                    >
                      {isOfferExpired(offer.expirationDate) && (
                        <Chip
                          label="EXPIRED"
                          color="error"
                          sx={{ 
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            fontWeight: 'bold'
                          }}
                        />
                      )}
                    </Box>
                  )}
                  
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" component="h2" gutterBottom noWrap>
                      {offer.title}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 2, 
                        flexGrow: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {offer.description}
                    </Typography>

                    {/* Price Information */}
                    {(offer.originalPrice || offer.finalPrice || offer.discountPercentage) && (
                      <Box sx={{ mb: 2 }}>
                        {offer.originalPrice && offer.finalPrice && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography 
                              variant="body2" 
                              sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                            >
                              ${offer.originalPrice}
                            </Typography>
                            <Typography variant="h6" color="success.main">
                              ${offer.finalPrice}
                            </Typography>
                            {offer.discountPercentage && (
                              <Chip 
                                label={`${offer.discountPercentage}% OFF`} 
                                size="small" 
                                color="success"
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Schedule sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                      <Typography 
                        variant="caption" 
                        color={isOfferExpired(offer.expirationDate) ? 'error' : 'text.secondary'}
                      >
                        {formatExpirationDate(offer.expirationDate)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        component={RouterLink}
                        to={`/offers/${offer.slug}`}
                        sx={{ flex: 1 }}
                      >
                        View Details
                      </Button>
                      
                      {!isOfferExpired(offer.expirationDate) && (
                        <Button
                          variant="contained"
                          size="small"
                          component="a"
                          href={offer.purchaseUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<ShoppingCart />}
                          sx={{ flex: 1 }}
                        >
                          Get Deal
                        </Button>
                      )}
                    </Box>

                    {offer.blogLink && (
                      <Button
                        variant="text"
                        size="small"
                        component="a"
                        href={offer.blogLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<Article />}
                        sx={{ mt: 1 }}
                      >
                        Read More
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
    </DashboardLayout>
  );
};

export default OffersByBrand; 